/* AdBanner — sponsored banner.
 *
 * Renders one image (or short looping video) that auto-resizes to fit the
 * available width on every device. The browser picks the natural aspect
 * ratio of the asset and we constrain to a sensible max-height so a vertical
 * portrait creative never overwhelms the viewport.
 *
 * Backend handles rotation/weighting/flight-dates. We just fetch + render.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

export default function AdBanner() {
  const [ad, setAd] = useState(null);

  const fetchAd = async () => {
    try {
      const { data } = await api.get("/ads/random");
      if (data) setAd(data);
      else setAd(null);
    } catch { /* swallow — banner is non-essential */ }
  };

  useEffect(() => {
    fetchAd();
    const id = setInterval(fetchAd, 20000);
    return () => clearInterval(id);
  }, []);

  if (!ad) return null;

  const href = ad.target_url || "#";
  const onClick = () => { api.post(`/ads/${ad.ad_id}/click`).catch(() => {}); };

  if (ad.image_url) {
    const isVideo = /\.(mp4|webm|mov|m4v|ogv)(\?|$)/i.test(ad.image_url);
    return (
      <a
        href={href}
        onClick={onClick}
        target="_blank"
        rel="noreferrer sponsored"
        data-testid="ad-banner"
        aria-label={ad.title || "Sponsored banner"}
        className="block w-full no-print bg-rf-ink/5"
      >
        {isVideo ? (
          <video
            src={ad.image_url}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="block mx-auto w-full max-w-7xl h-auto max-h-[200px] sm:max-h-[160px] object-contain"
          />
        ) : (
          <img
            src={ad.image_url}
            alt={ad.title || ""}
            loading="lazy"
            className="block mx-auto w-full max-w-7xl h-auto max-h-[200px] sm:max-h-[160px] object-contain"
          />
        )}
      </a>
    );
  }

  // Text-only fallback when no image is configured
  return (
    <a
      href={href}
      onClick={onClick}
      target="_blank"
      rel="noreferrer sponsored"
      data-testid="ad-banner"
      className="block w-full no-print bg-rf-amber/15 hover:bg-rf-amber/25 transition-colors"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-3">
        <p className="text-sm font-bold text-rf-ink text-center truncate">
          {ad.title || "Featured product"}
        </p>
      </div>
    </a>
  );
}
